<script setup lang="ts" generic="T extends object">
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { IMenuItem, IPagination } from "@/types"
import { attachClosestEdge, extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { preserveOffsetOnSource } from "@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source"
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview"
import isEmpty from "lodash-es/isEmpty"
import isEqual from "lodash-es/isEqual"
import xorWith from "lodash-es/xorWith"
import { computed, nextTick, onUnmounted, ref, watch } from "vue"
import { Checkbox, DropdownMenu, Icon, NoData, Pagination, Tooltip } from "@/components/ui"
import { useTranslation } from "@/composables"

interface RowMoveConstraints {
  canMoveUp: boolean
  canMoveDown: boolean
  canMoveToStart: boolean
  canMoveToEnd: boolean
}

interface RowDragState {
  isDragging: boolean
  closestEdge: "top" | "bottom" | null
}

const props = withDefaults(defineProps<{
  headers?: ITableHeader<T>[]
  data?: T[]
  isLoading?: boolean
  showPagination?: boolean
  pagination?: Partial<IPagination>
  showSelect?: boolean
  isDraggable?: boolean
  dense?: boolean
  title?: string
  noDataHeadline?: string
  noDataContent?: string
  modelValue?: T[]
  isRowExpandable?: (row: T, index: number) => boolean
  getRowLabel?: (row: T, index: number) => string
  getRowMoveConstraints?: (row: T, index: number) => RowMoveConstraints
}>(), {
  headers: () => [],
  data: () => [],
  pagination: undefined,
  showPagination: false,
  showSelect: false,
  isDraggable: false,
  dense: false,
  title: "",
  noDataHeadline: undefined,
  noDataContent: undefined,
  modelValue: () => [],
  isRowExpandable: undefined,
  getRowLabel: undefined,
  getRowMoveConstraints: undefined,
})

const emit = defineEmits<{
  "update:modelValue": [selection: T[]]
  "paginationPrevious": [current: number]
  "paginationNext": [current: number]
  "rowDrop": [fromIndex: number, toIndex: number]
  "rowDropAbsolute": [fromIndex: number, position: "start" | "end"]
}>()

const { t } = useTranslation()

function rowLabel(item: T, index: number): string {
  return props.getRowLabel?.(item, index) ?? t("table.row_n", { n: index + 1 })
}

function rowMoveConstraints(item: T, index: number): RowMoveConstraints {
  if (props.getRowMoveConstraints) {
    return props.getRowMoveConstraints(item, index)
  }

  return {
    canMoveUp: index > 0,
    canMoveDown: index < (props.data?.length ?? 0) - 1,
    canMoveToStart: index > 0,
    canMoveToEnd: index < (props.data?.length ?? 0) - 1,
  }
}

function rowMoveMenuItems(item: T, index: number): IMenuItem[] {
  const constraints = rowMoveConstraints(item, index)
  return [
    {
      key: "move-to-start",
      label: t("table.move_to_start"),
      onClick: () => emit("rowDropAbsolute", index, "start"),
      dismissOnInteract: true,
      isHidden: !constraints.canMoveToStart,
    },
    {
      key: "move-up",
      label: t("table.move_up"),
      onClick: () => emit("rowDrop", index, index - 1),
      dismissOnInteract: true,
      isHidden: !constraints.canMoveUp,
    },
    {
      key: "move-down",
      label: t("table.move_down"),
      onClick: () => emit("rowDrop", index, index + 1),
      dismissOnInteract: true,
      isHidden: !constraints.canMoveDown,
    },
    {
      key: "move-to-end",
      label: t("table.move_to_end"),
      onClick: () => emit("rowDropAbsolute", index, "end"),
      dismissOnInteract: true,
      isHidden: !constraints.canMoveToEnd,
    },
  ]
}

const expandedRows = ref<number[]>([])

const rowDragStates = ref<RowDragState[]>(
  (props.data ?? []).map(() => ({ isDragging: false, closestEdge: null })),
)
const rowRefs = ref<(HTMLElement | null)[]>([])
const dragCleanups = ref<(() => void)[]>([])

function buildRowDragStates() {
  rowDragStates.value = (props.data ?? []).map(() => ({ isDragging: false, closestEdge: null }))
}

function setRowRef(el: HTMLElement | null, index: number) {
  rowRefs.value[index] = el
}

function teardownDrag() {
  dragCleanups.value.forEach(fn => fn())
  dragCleanups.value = []
}

function wireDrag() {
  if (!props.isDraggable)
    return

  teardownDrag()

  rowRefs.value.forEach((rowEl, index) => {
    if (!rowEl)
      return

    const handleEl = rowEl.querySelector<HTMLElement>("[data-drag-handle]")
    if (!handleEl)
      return

    const cleanupDraggable = draggable({
      element: rowEl,
      dragHandle: handleEl,
      getInitialData: () => ({ index }),
      onGenerateDragPreview({ nativeSetDragImage, location }) {
        setCustomNativeDragPreview({
          getOffset: preserveOffsetOnSource({ element: rowEl, input: location.current.input }),
          render({ container }) {
            const previewTable = document.createElement("table")
            previewTable.style.borderCollapse = "collapse"
            previewTable.style.backgroundColor = "white"
            previewTable.style.borderRadius = "0.5rem"
            previewTable.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"
            const tbody = document.createElement("tbody")
            const clone = rowEl.cloneNode(true) as HTMLElement
            clone.style.opacity = "1"
            tbody.appendChild(clone)
            previewTable.appendChild(tbody)
            container.appendChild(previewTable)
          },
          nativeSetDragImage,
        })
      },
      onDragStart() {
        rowDragStates.value[index] = { ...rowDragStates.value[index], isDragging: true }
      },
      onDrop() {
        rowDragStates.value[index] = { ...rowDragStates.value[index], isDragging: false }
      },
    })

    const cleanupDropTarget = dropTargetForElements({
      element: rowEl,
      canDrop({ source }) {
        return source.data?.index !== index
      },
      getData({ input }) {
        return attachClosestEdge({ index }, {
          element: rowEl,
          input,
          allowedEdges: ["top", "bottom"],
        })
      },
      onDrag({ self }) {
        const edge = extractClosestEdge(self.data)
        rowDragStates.value[index] = {
          ...rowDragStates.value[index],
          closestEdge: edge === "top" || edge === "bottom" ? edge : null,
        }
      },
      onDragLeave() {
        rowDragStates.value[index] = { ...rowDragStates.value[index], closestEdge: null }
      },
      onDrop({ source, self }) {
        rowDragStates.value[index] = { ...rowDragStates.value[index], closestEdge: null }
        const fromIndex = source.data?.index
        if (typeof fromIndex !== "number")
          return

        const edge = extractClosestEdge(self.data)
        let toIndex = index
        if (edge === "bottom")
          toIndex = index + 1

        // Adjust for the gap created by removing the source item
        const adjustedTo = fromIndex < toIndex ? toIndex - 1 : toIndex
        if (adjustedTo !== fromIndex)
          emit("rowDrop", fromIndex, adjustedTo)
      },
    })

    dragCleanups.value.push(cleanupDraggable, cleanupDropTarget)
  })
}

watch(
  () => props.data,
  async () => {
    buildRowDragStates()
    expandedRows.value = expandedRows.value.filter(i => i < (props.data?.length ?? 0))
    if (props.isDraggable) {
      await nextTick()
      wireDrag()
    }
  },
  { deep: true },
)

watch(
  () => props.isDraggable,
  async (val) => {
    if (val) {
      await nextTick()
      wireDrag()
    }
    else {
      teardownDrag()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  teardownDrag()
})

function onPaginationPrevious(current: number) {
  emit("paginationPrevious", current)
}
function onPaginationNext(current: number) {
  emit("paginationNext", current)
}

function onRowSelect(data: T) {
  const currentSelection = props.modelValue
  const uniqueSelected = new Set([data, ...currentSelection])
  emit("update:modelValue", Array.from(uniqueSelected))
}

function onRowDeselect(data: T) {
  const currentSelection = props.modelValue
  const uniqueSelected = new Set(currentSelection.filter((d: T) => !isEqual(data, d)))
  emit("update:modelValue", Array.from(uniqueSelected))
}

function onSelectPage() {
  const currentSelection = props.modelValue
  const uniqueSelection = new Set([...currentSelection, ...props.data])
  emit("update:modelValue", Array.from(uniqueSelection))
}

function onDeselectPage() {
  emit("update:modelValue", [])
}

function isRowExpandable(item: T, index: number) {
  return props.isRowExpandable?.(item, index) ?? false
}

function rowIsExpanded(index: number) {
  return expandedRows.value.includes(index)
}

function rowHasBottomBorder(index: number) {
  return index < props.data.length - 1
}

function showMainRowBorder(index: number) {
  return rowHasBottomBorder(index) && !rowIsExpanded(index)
}

function toggleExpandedRow(index: number) {
  expandedRows.value = rowIsExpanded(index)
    ? expandedRows.value.filter(value => value !== index)
    : [...expandedRows.value, index]
}

function getExpandableRowIndexes() {
  return props.data
    .map((item, index) => isRowExpandable(item, index) ? index : -1)
    .filter(index => index >= 0)
}

function expandAllRows() {
  expandedRows.value = getExpandableRowIndexes()
}

function collapseAllRows() {
  expandedRows.value = []
}

const pageIsSelected = computed(() => isEmpty(xorWith(props.modelValue, props.data, isEqual)))
function rowIsSelected(item: T) {
  return computed(() => Boolean(props.modelValue.filter((d: T) => isEqual(d, item)).length))
}

const partialPageSelected = computed(() => props.modelValue.length > 0 && props.modelValue.length < props.data.length)
const hasExpandableRows = computed(() => props.data.some((item, index) => isRowExpandable(item, index)))
const detailColspan = computed(() => props.headers.length + (props.showSelect ? 1 : 0) + (hasExpandableRows.value ? 1 : 0) + (props.isDraggable ? 1 : 0))
const allExpandableRowsExpanded = computed(() => {
  const expandableRowIndexes = getExpandableRowIndexes()

  return expandableRowIndexes.length > 0 && expandableRowIndexes.every(index => expandedRows.value.includes(index))
})

function toggleExpandAllRows() {
  if (allExpandableRowsExpanded.value) {
    collapseAllRows()
    return
  }

  expandAllRows()
}

defineExpose({
  expandAllRows,
  collapseAllRows,
  toggleExpandAllRows,
})
</script>

<template>
  <div class="shadow-xs border-secondary border rounded-xl bg-white">
    <slot name="header">
      <div v-if="title" class="border-b border-secondary rounded-t-xl">
        <h2 class="py-3 pl-3 font-bold font-comfortaa">
          {{ title }}
        </h2>
      </div>
    </slot>
    <div class="flex flex-col overflow-x-auto overscroll-x-contain relative" :class="!title && !$slots.header ? 'rounded-xl' : 'rounded-b-xl'">
      <!-- Loading state -->
      <span role="status" aria-live="polite" class="sr-only">{{ isLoading ? t('table.loading') : '' }}</span>
      <table v-if="isLoading" aria-hidden="true" :aria-label="title || undefined" class="min-w-full border-collapse">
        <thead>
          <tr>
            <th v-for="n in 4" :key="n" scope="col" class="bg-footer-bg border-secondary border-b first:pl-3 last:pr-3 px-1.5 py-3">
              <div class="gpx-skeleton w-32 h-6" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="n in 5" :key="n" class="px-3 border-b last:border-b-0 border-secondary">
            <td v-for="m in 4" :key="m" class="first:pl-3 last:pr-3 px-1.5" :class="dense ? 'py-1.5' : 'py-4'">
              <div class="gpx-skeleton w-48 h-6" />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Data table -->
      <table v-else :aria-label="title || undefined" class="border-collapse w-full">
        <thead class="font-comfortaa whitespace-nowrap capitalize">
          <tr>
            <th v-if="isDraggable" scope="col" class="bg-footer-bg border-secondary border-b px-1.5 py-3 w-10" />
            <th v-if="showSelect" scope="col" class="bg-footer-bg border-secondary border-b first:pl-3 last:pr-3 px-1.5 py-3">
              <Checkbox
                name="gpx-table-select-page"
                :indeterminate="partialPageSelected"
                :label="t('table.page_select_toggle')"
                :show-label="false"
                :model-value="pageIsSelected"
                @input="
                  (event: Event) => ((event.target as HTMLInputElement).checked ? onSelectPage() : onDeselectPage())
                "
              />
            </th>
            <th v-if="hasExpandableRows" scope="col" class="bg-footer-bg border-secondary border-b px-1.5 py-3 w-12 text-center text-xs text-gray-800">
              <Tooltip :content="t('table.expand_all_rows_tooltip')">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-700 transition"
                  :aria-label="allExpandableRowsExpanded ? t('table.collapse_all_rows') : t('table.expand_all_rows')"
                  @click="toggleExpandAllRows"
                >
                  <Icon icon="UnfoldVertical" size="sm" />
                </button>
              </Tooltip>
            </th>
            <th
              v-for="(header, index) in headers"
              :key="`header-${index}`"
              scope="col"
              class="bg-footer-bg border-secondary border-b first:pl-3 last:pr-3 px-1.5 py-3 text-left text-xs text-gray-800"
            >
              <slot
                v-if="$slots[`header-${String(header?.value)}`]"
                :name="`header-${String(header?.value)}`"
                v-bind="{ header }"
              />
              <slot v-else-if="$slots['header-row']" name="header-row" v-bind="{ header }" />
              <span v-else>{{ header?.label }}</span>
            </th>
          </tr>
        </thead>

        <tbody v-if="data.length" class="text-gray-700">
          <template v-for="(item, index) in data" :key="`data-${index}`">
            <tr
              :id="`gpx-table-row-${index}`"
              :ref="(el) => setRowRef(el as HTMLElement | null, index)"
              class="px-3"
              :class="{
                'opacity-50': rowDragStates[index]?.isDragging,
                'border-t-2 border-t-primary': isDraggable && rowDragStates[index]?.closestEdge === 'top',
                'border-b-2 border-b-primary': isDraggable && rowDragStates[index]?.closestEdge === 'bottom',
              }"
            >
              <td v-if="isDraggable" class="px-1.5 w-10" :class="showMainRowBorder(index) ? 'border-b border-secondary' : ''">
                <DropdownMenu :items="rowMoveMenuItems(item, index)">
                  <template #activator="{ isOpen }">
                    <button
                      type="button"
                      data-drag-handle
                      class="inline-flex h-8 w-8 items-center justify-center rounded transition-colors touch-none"
                      :class="isOpen ? 'bg-link text-white' : 'text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600'"
                      :aria-label="t('table.drag_row', { label: rowLabel(item, index) })"
                    >
                      <Icon icon="GripVertical" size="sm" />
                    </button>
                  </template>
                </DropdownMenu>
              </td>
              <td v-if="showSelect && !$slots[`data-select`]" class="first:pl-3 px-1.5" :class="showMainRowBorder(index) ? 'border-b border-secondary' : ''">
                <Checkbox
                  :name="`gpx-table-row-select-${index}`"
                  :model-value="rowIsSelected(item).value"
                  :label="t('table.select_row', { label: rowLabel(item, index) })"
                  :show-label="false"
                  @input="
                    (event: Event) =>
                      (event.target as HTMLInputElement).checked ? onRowSelect(item) : onRowDeselect(item)
                  "
                />
              </td>
              <td v-if="hasExpandableRows" class="px-1.5 w-12" :class="showMainRowBorder(index) ? 'border-b border-secondary' : ''">
                <button
                  v-if="isRowExpandable(item, index)"
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-700 transition"
                  :aria-expanded="rowIsExpanded(index)"
                  :aria-label="rowIsExpanded(index) ? t('table.collapse_row_details', { label: rowLabel(item, index) }) : t('table.expand_row_details', { label: rowLabel(item, index) })"
                  @click="toggleExpandedRow(index)"
                >
                  <Icon icon="CircleChevronDown" size="sm" class="transition-transform duration-200" :class="rowIsExpanded(index) ? 'rotate-180' : ''" />
                </button>
              </td>
              <td
                v-for="(column, columnIndex) in headers"
                :key="`cell-${columnIndex}`"
                class="whitespace-nowrap text-xs md:text-sm first:pl-3 last:pr-3 px-1.5"
                :class="[dense ? 'py-1.5' : 'py-4', showMainRowBorder(index) ? 'border-b border-secondary' : '']"
              >
                <slot :name="`data-${String(column?.value)}`" v-bind="{ data: item }">
                  {{ column?.value && column.value !== "actions" && column.value in item ? item[column.value]?.toString() : "—" }}
                </slot>
              </td>
            </tr>
            <tr
              v-if="isRowExpandable(item, index) && rowIsExpanded(index)"
              :aria-labelledby="`gpx-table-row-${index}`"
            >
              <td :colspan="detailColspan" class="px-3 py-4" :class="rowHasBottomBorder(index) ? 'border-b border-secondary' : ''">
                <slot name="expanded-row" v-bind="{ data: item, index }" />
              </td>
            </tr>
          </template>
        </tbody>
        <slot v-else name="empty">
          <tbody>
            <tr>
              <td :colspan="detailColspan">
                <NoData :headline="noDataHeadline" :content="noDataContent" hide-button />
              </td>
            </tr>
          </tbody>
        </slot>
      </table>
    </div>
    <slot name="pagination">
      <div v-if="showPagination" class="border-t border-secondary py-3 px-3">
        <Pagination
          :current-page="pagination?.page?.value"
          :page-count="pagination?.pageCount?.value"
          :can-next="pagination?.canNext?.value"
          :can-previous="pagination?.canPrevious?.value"
          @previous="onPaginationPrevious"
          @next="onPaginationNext"
        />
      </div>
    </slot>
    <slot name="footer" />
  </div>
</template>
